import {
  type CSSProperties,
  type PointerEvent,
  type ReactNode,
  useRef
} from "react";
import { resetCardPointerState } from "../../lib/card-pointer-state";

interface TiltedSpotlightCardProps {
  children: ReactNode;
  className?: string;
  dataModelId: string;
}

interface CardStyle extends CSSProperties {
  "--card-shadow-x": string;
  "--card-shadow-y": string;
  "--tilt-x": string;
  "--tilt-y": string;
  "--tilt-scale": string;
  "--spotlight-x": string;
  "--spotlight-y": string;
}

const restingStyle: CardStyle = {
  "--card-shadow-x": "0px",
  "--card-shadow-y": "6px",
  "--tilt-x": "0deg",
  "--tilt-y": "0deg",
  "--tilt-scale": "1",
  "--spotlight-x": "50%",
  "--spotlight-y": "50%"
};

const MAX_TILT = 6;

function prefersReducedMotion() {
  return window.matchMedia("(prefers-reduced-motion: reduce)").matches;
}

/**
 * Combines the pointer geometry of React Bits Tilted Card and Spotlight Card.
 * The outer shell is reserved for GSAP list transitions; this inner wrapper
 * exclusively owns the pointer-driven transform.
 */
export function TiltedSpotlightCard({
  children,
  className = "",
  dataModelId
}: TiltedSpotlightCardProps) {
  const shell = useRef<HTMLDivElement>(null);
  const card = useRef<HTMLDivElement>(null);

  const handlePointerMove = (event: PointerEvent<HTMLDivElement>) => {
    const shellElement = shell.current;
    const element = card.current;
    if (!shellElement || !element) return;

    const bounds = shellElement.getBoundingClientRect();
    const x = event.clientX - bounds.left;
    const y = event.clientY - bounds.top;
    if (x < 0 || y < 0 || x > bounds.width || y > bounds.height) {
      resetTilt();
      return;
    }

    shellElement.dataset.wheelActive = "true";
    element.dataset.active = "true";
    element.style.setProperty("--spotlight-x", `${x}px`);
    element.style.setProperty("--spotlight-y", `${y}px`);

    if (prefersReducedMotion()) return;
    element.style.setProperty("--tilt-scale", "1.025");
    const xRatio = x / bounds.width - 0.5;
    const yRatio = y / bounds.height - 0.5;
    element.style.setProperty(
      "--tilt-x",
      `${(-yRatio * MAX_TILT * 2).toFixed(2)}deg`
    );
    element.style.setProperty(
      "--tilt-y",
      `${(xRatio * MAX_TILT * 2).toFixed(2)}deg`
    );
    element.style.setProperty(
      "--card-shadow-x",
      `${(-xRatio * 10).toFixed(1)}px`
    );
    element.style.setProperty(
      "--card-shadow-y",
      `${(6 - yRatio * 8).toFixed(1)}px`
    );
  };

  const resetTilt = () => {
    const shellElement = shell.current;
    if (shellElement) resetCardPointerState(shellElement);
  };

  return (
    <div
      ref={shell}
      className={`model-card-shell ${className}`.trim()}
      data-model-id={dataModelId}
      style={restingStyle}
      onPointerEnter={handlePointerMove}
      onPointerMove={handlePointerMove}
      onPointerLeave={resetTilt}
    >
      <div ref={card} className="model-card-tilt">
        {children}
      </div>
    </div>
  );
}
